import React, { ReactElement } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Filter,
  Table,
  TableContainer,
  TableProps,
} from '@odin-labs/components';
import { paginationSizePerPage as limit } from 'utils/constants';
import {
  GetJobsiteContainerContractorsDocument,
  useGetJobsiteContainerContractorsQuery,
} from 'apollo/generated/client-operations';
import { AuthContext } from 'auth';
import { getOrganizationName, navigateTo, useModalState } from 'utils';
import { LoadingError } from 'components/loadingError';
import { JobsiteTabProps } from 'containers/jobsite/types';
import {
  EditContractorAssignmentModal,
  RemoveContractorAssignmentModal,
  OffboardJobsiteContractorModal,
  ReinstateJobsiteContractorModal,
} from 'containers/jobsite/modals';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { getColumns, getFilterItems } from './tables';
import {
  JobsiteContractor,
  JobsiteContractorFilters,
  JobsiteContractorColumn,
  JobsiteContractorModalValue,
} from './types';

export function JobsiteContractorsTabContainer(
  props: JobsiteTabProps,
): ReactElement {
  const { jobsiteId, isProcoreEnabled, procoreProjectVendors, onTabApiChange } =
    props;
  const { currentUser: user } = React.useContext(AuthContext);
  const history = useHistory();

  const {
    value: jobsiteContractorModalValue,
    isModalOpen,
    openModal,
    closeModal,
    resetModalValue: resetJobsiteContractorModalValue,
  } = useModalState<JobsiteContractorModalValue>(null);
  const { jobsiteContractor, modalAction } = jobsiteContractorModalValue ?? {};

  const { page, search, procoreStatus, updateUrl } = usePageQueryParams();
  const offset = page * limit;

  const { data, loading, error, refetch } =
    useGetJobsiteContainerContractorsQuery({
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables: {
        jobsiteId,
        jobsiteJobsiteContractorsInput: {
          paginationInput: { limit, offset },
          search,
          procoreLinked: procoreStatus ? procoreStatus === 'linked' : undefined,
        },
      },
    });

  const jobsite = data?.getJobsite;
  const isProcoreConnected =
    data?.getProcoreConnectionStatus?.connected ?? false;

  const columns = React.useMemo<JobsiteContractorColumn[]>(
    () => getColumns({ user, openModal }),
    [user, openModal],
  );
  const filterItems = React.useMemo(
    () => getFilterItems({ search, isProcoreEnabled, procoreStatus }),
    [search, isProcoreEnabled, procoreStatus],
  );

  const onPageChangeHandler = (_pageSize: number, pageIndex: number): void => {
    updateUrl({
      page: pageIndex ? pageIndex + 1 : null,
    });
  };

  const onFiltersChangeHandler = (
    changedFilters: Partial<JobsiteContractorFilters>,
  ): void => {
    updateUrl({ page: null, ...changedFilters });
  };

  const onRowClickHandler: TableProps<JobsiteContractor>['onRowClick'] = ({
    data: rowJobsiteContractor,
    event,
  }): void => {
    const path = `/contractor/${rowJobsiteContractor?.contractor?.contractorId}`;
    navigateTo(path, history, event);
  };

  const jobsiteContractors =
    jobsite?.jobsiteContractors.edges.map((c) => c.node) ?? [];
  const jobsiteContractorsCount = jobsite?.jobsiteContractors?.count ?? 0;
  const pageCount = Math.ceil(jobsiteContractorsCount / limit);
  const jobsiteContractorsCountText = jobsite
    ? jobsiteContractorsCount.toString()
    : '...';

  React.useEffect(
    () =>
      onTabApiChange({
        refetchData: refetch,
        contractorsCountText: jobsiteContractorsCountText,
      }),
    [refetch, jobsiteContractorsCountText],
  );

  if (error) {
    return <LoadingError error={error} />;
  }

  const contractorName = getOrganizationName(
    jobsiteContractor?.contractor?.organization,
  );

  return (
    <TableContainer className="jobsite-contractors-container">
      <Filter
        items={filterItems}
        loading={loading}
        firstItemOnRight="search"
        onChange={onFiltersChangeHandler}
      />
      <Table
        loading={loading}
        columns={columns}
        data={jobsiteContractors}
        initialState={{ pageSize: limit }}
        pageCount={pageCount}
        pageIndex={page}
        remote
        enablePagination
        onRowClick={onRowClickHandler}
        onPageChange={onPageChangeHandler}
        disableGlobalFilter
      />
      <EditContractorAssignmentModal
        jobsiteContractor={jobsiteContractor}
        isOpen={isModalOpen && modalAction === 'edit'}
        closeModal={closeModal}
        onClosed={resetJobsiteContractorModalValue}
        jobsite={jobsite}
        contractors={[jobsiteContractor?.contractor].filter(Boolean)}
        isProcoreEnabled={isProcoreEnabled}
        isProcoreConnected={isProcoreConnected}
        procoreProjectVendors={procoreProjectVendors}
      />
      <RemoveContractorAssignmentModal
        isOpen={isModalOpen && modalAction === 'remove'}
        closeModal={closeModal}
        onClosed={resetJobsiteContractorModalValue}
        jobsiteContractorId={jobsiteContractor?.id}
      />
      <OffboardJobsiteContractorModal
        isOpen={isModalOpen && modalAction === 'offboard'}
        closeModal={closeModal}
        onClosed={resetJobsiteContractorModalValue}
        jobsiteContractorId={jobsiteContractor?.id}
        jobsiteName={jobsite?.name}
        contractorName={contractorName}
        workersCount={jobsiteContractor?.jobsiteWorkers.count ?? 0}
        refetchQueries={[GetJobsiteContainerContractorsDocument]}
      />
      <ReinstateJobsiteContractorModal
        isOpen={isModalOpen && modalAction === 'reinstate'}
        closeModal={closeModal}
        onClosed={resetJobsiteContractorModalValue}
        jobsiteContractorId={jobsiteContractor?.id}
        jobsiteName={jobsite?.name}
        contractorName={contractorName}
        workersCount={jobsiteContractor?.jobsiteWorkers.count ?? 0}
        refetchQueries={[GetJobsiteContainerContractorsDocument]}
      />
    </TableContainer>
  );
}
